import axios from "axios";

const normalizeBaseUrl = (url: string) => {
  const normalized = url.replace(/\/+$/, "");
  return /\/api$/i.test(normalized) ? `${normalized}/spare-parts` : normalized;
};

const fallbackBaseUrl = "/api/spare-parts-service/api/v1";

export const sparePartsApi = axios.create({
  baseURL: normalizeBaseUrl(
    import.meta.env.VITE_SPARE_PARTS_API_URL || fallbackBaseUrl
  ),
  timeout: 30000,
});

type StoredUser = {
  id?: string;
  userId?: string;
  employeeId?: string;
  employeeRecordId?: string;
  email?: string;
  user?: {
    id?: string;
    email?: string;
  };
};

const getStoredUser = (): StoredUser | null => {
  try {
    const value = localStorage.getItem("user");
    return value ? (JSON.parse(value) as StoredUser) : null;
  } catch {
    return null;
  }
};

sparePartsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  const user = getStoredUser();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // The Spare Parts service can resolve the current role directly from these
  // authenticated dashboard identifiers instead of waiting on the employee directory.
  const employeeId = user?.employeeRecordId || user?.id || user?.employeeId;
  const userId = user?.userId || user?.user?.id || user?.id;
  const email = user?.email || user?.user?.email;

  if (employeeId) {
    config.headers["x-employee-id"] = employeeId;
  }

  if (userId) {
    config.headers["x-user-id"] = userId;
  }

  if (email) {
    config.headers["x-user-email"] = email;
  }

  return config;
});

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: unknown;
}

export async function getSparePartsData<T>(
  url: string,
  params?: Record<string, unknown>
): Promise<T> {
  const response = await sparePartsApi.get<ApiResponse<T>>(url, {
    params,
  });

  return response.data.data;
}

export default sparePartsApi;
